import React, {useMemo} from "react";
import * as Yup from "yup";
import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {BrokerService} from "../../../../services/broker.service";
import {Col, Label, Modal, ModalBody, ModalHeader, Row} from "reactstrap";
import {useFormik} from "formik";
import {TrafficOrderService} from "../../../../services/traffic_order.service";
import Select from "../../../../components/Common/Select";

interface IAssignIntegra {
    isOpen: boolean;
    toggle: () => void;
    id: string;
    crmId: number;
}

const validationSchema = Yup.object({
    broker_id: Yup.number().required("Broker must be selected"),
});

interface AssignBroker {
    broker_id: number;
}

const AssignIntegra: React.FC<IAssignIntegra> = ({isOpen, toggle, id, crmId}) => {
    const queryClient = useQueryClient();

    const onSuccess = () => {
        queryClient.invalidateQueries({queryKey: ["keyTrafficOrders"]})
            .then(() => toggle())
    }

    const selectBrokers = (brokers: any[]) =>
        brokers.map((broker) => ({value: broker.value, label: broker.label, office_id: broker.office_id}))

    const {data: brokersSelect, isLoading: isLoadingBrokers} = useQuery({
        queryKey: ["keyBrokerList", crmId], queryFn: () => BrokerService.getBrokerByCrmIdList(crmId),
        select: selectBrokers
    });

    const {data: brokerOrder, isFetching: isLoadingBrokerOrder} = useQuery({
        queryKey: ["keyBrokerTrafficOrder", id], queryFn: () => TrafficOrderService.getBrokerTrafficOrder(+id),
    });

    const {mutate: updateBroker, isPending: isPendingUpdate} = useMutation({
        mutationFn: (data: AssignBroker) => TrafficOrderService.updateBrokerTrafficOrder(data, +id),
        mutationKey: ["keyUpdateBrokerTrafficOrder", id],
        onSuccess: onSuccess
    });

    const initialValues = useMemo(() => ({
        broker_id: brokerOrder?.broker_id as number || null,
    }), [brokerOrder]);

    const handleSubmitForm = (values: any) => {
        updateBroker(values);
    }

    const {
        values,
        handleSubmit,
        isValid,
        setFieldValue,
        resetForm,
        errors,
        touched
    } = useFormik({
        initialValues: initialValues,
        onSubmit: handleSubmitForm,
        validationSchema: validationSchema,
        validateOnBlur: true,
        validateOnChange: true,
        validateOnMount: true,
        enableReinitialize: true
    });

    const handleChangeBroker = (selected: any) => {
        setFieldValue("broker_id", selected.value);
    }

    const selectedValue = useMemo(() =>
            brokersSelect?.find((b: any) => b.value === values.broker_id) || null
        , [values.broker_id, brokersSelect]);

    const closeModal = () => {
        toggle();
        resetForm();
    };

    const isLoading = isLoadingBrokers || isLoadingBrokerOrder;
    const isSaving = isPendingUpdate || !!queryClient.isFetching({queryKey: ["keyTrafficOrders"]});

    return (
        <Modal isOpen={isOpen} toggle={closeModal}>
            <ModalHeader toggle={closeModal}>Assign Integra Order {id}</ModalHeader>
            <ModalBody>
                <form onSubmit={handleSubmit}>
                    <Row>
                        <Col xs={12}>
                            <div className="mb-4">
                                <Label>Select broker: </Label>
                                <Select
                                    isSearchable
                                    name="broker_id"
                                    label="Brokers"
                                    placeholder="Select broker..."
                                    isLoading={isLoading}
                                    isDisabled={isLoading || isSaving}
                                    options={brokersSelect}
                                    value={!isLoading && selectedValue}
                                    onChange={handleChangeBroker}
                                />
                                {!!(errors.broker_id && touched.broker_id) && (
                                    <div className="invalid-feedback d-block m-0">{errors.broker_id}</div>)}
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col className="text-end">
                            <button type="submit" className="btn btn-success"
                                    disabled={!isValid || isLoading || isSaving}>
                                {isSaving ? "Loading..." : "Save"}
                            </button>
                        </Col>
                    </Row>
                </form>
            </ModalBody>
        </Modal>
    );
};

export default AssignIntegra;